import {
  PrimaryButton,
  Section,
  SectionDescription,
  SectionTitle,
} from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'

import {
  getCurrentQuote,
  getUsablePaymentMethod,
  PaymentMethod,
  PaymentMethodType,
} from '../../../../../domain'
import { useApiErrorHandlingAsyncCallback } from '../../../../../hooks/auth'
import { QuotePremiums, useQuotePremiums } from '../../../../../hooks/premiums'
import {
  useStripePaymentAdmin,
  useStripePaymentUser,
} from '../../../../../hooks/stripePayment'
import { useStripeSetup } from '../../../../../hooks/stripeSetup'
import {
  TKey,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { PaymentScheduleConfig } from '../../../../../state/configuration'
import { usePrincipal } from '../../../../authentication'
import { usePaymentMethods } from '../../../policy/billing'
import { useProduct } from '../../../productContext'
import { useProposal } from '../../ProposalProvider'
import { PaymentPath } from '../PaymentPath'

export interface PaymentCardProps {
  config: PaymentScheduleConfig
}

const tKey = TKey('components.payment-card')

export const PaymentCard: FunctionComponent<PaymentCardProps> = ({ config }) => {
  const { t } = useWriskTranslation()

  const { apiClient, isAdmin } = usePrincipal()

  const { proposal, dispatch, refresh } = useProposal()
  const { product } = useProduct()
  const { paymentMethods } = usePaymentMethods()

  const quote = getCurrentQuote(proposal)

  const paymentMethod = getUsablePaymentMethod(paymentMethods, PaymentMethodType.CARD)
  const premiums = useQuotePremiums(quote, product)

  const paymentType = getPaymentType(premiums, paymentMethod)

  const onStripeUserPayment = useStripePaymentUser({
    saveCard: true,
    proposalCode: proposal.proposalCode,
    successPath: [location.pathname, PaymentPath.PAYMENT_CALLBACK].join('/'),
    cancelPath: location.pathname,
  })

  const onStripeAdminPayment = useStripePaymentAdmin({
    saveCard: true,
    proposalCode: proposal.proposalCode,
    onSuccess: refresh,
  })

  const onSetupStripe = useStripeSetup({
    clientReference: proposal.proposalCode,
    successPath: location.pathname,
    cancelPath: location.pathname,
  })

  const onPay = useApiErrorHandlingAsyncCallback(async () => {
    switch (paymentType) {
      case 'PAY':
        return isAdmin
          ? await onStripeAdminPayment.execute()
          : await onStripeUserPayment.execute()
      case 'SETUP':
        return await onSetupStripe.execute()
      case 'BIND':
        dispatch({
          type: 'UPDATE_PROPOSAL',
          payload: await apiClient.bindProposal(proposal.proposalCode, {
            paymentMethodType: PaymentMethodType.CARD,
          }),
        })
    }
  })

  return (
    <>
      <Section>
        <SectionTitle>{t(tKey('title'))}</SectionTitle>
        <SectionDescription>
          {t([
            tKey('description', config.type, paymentType),
            tKey('description', config.type),
            tKey('description'),
          ])}
        </SectionDescription>
      </Section>
      <PrimaryButton
        data-testid='bind-proposal'
        layout='fixed'
        onClick={onPay.execute}
        loading={onPay.loading}
      >
        {t([
          tKey('action', config.type, paymentType),
          tKey('action', config.type),
          tKey('action'),
        ])}
      </PrimaryButton>
    </>
  )
}

const getPaymentType = (
  premiums: QuotePremiums,
  paymentMethod: PaymentMethod | undefined,
): 'PAY' | 'BIND' | 'SETUP' =>
  premiums.totals.discounted > 0 ? 'PAY' : paymentMethod ? 'BIND' : 'SETUP'
